import React, {useCallback, useEffect, useMemo, useState} from "react";
import _ from "underscore";
import {bindActionCreators} from "redux";
import {connect} from "react-redux";
import {push} from "react-router-redux";
import requestor from "requestor";
import {RouterState, InjectedRouter} from "react-router/lib/Router";

import CircularProgress from "@material-ui/core/CircularProgress";

import Permissioner from "utils/permissioner";
import {isInitialised} from "utils/uninitialised";
import setTitle from "utils/set_title";

import LlmRunCompareComponent from "../components/llm_run_compare";

import llmRunsCompareFetch, {
  GetLlmRunsCompareAction,
} from "modules/flowmaster/actions/llm_runs_compare_fetch";

import {User} from "common/types/user";
import {
  LlmRunFilters,
  LlmRunForComparison,
} from "common/flowmaster/types/llm_run";
import pickLlmRunFiltersFromURL from "routes/llm_run/utils/filtering/pick_llm_run_filters_from_url";
import {getLlmRunQueryParams} from "routes/llm_run/utils/filtering/get_llm_run_query_params";
import formatDateToLocalISOString from "routes/llm_run/utils/format_date_local_iso_string";
import {buildQuery, parseQuery} from "utils/uri";
import getNewSearchParams from "routes/llm_run/utils/filtering/update_url_params";

type Props = {
  llmRuns: LlmRunForComparison[];
  user: User;
  router: RouterState & InjectedRouter;
  location: Location;
  params: {
    organisationId: string;
  };
  llmRunsCompareFetch: GetLlmRunsCompareAction["action"];
  dispatch: (args: unknown) => unknown;
};

function LlmRunCompareContainer(props: Props) {
  const llmRunValues = getLlmRunQueryParams(
    pickLlmRunFiltersFromURL(props.router),
  );

  const maxRuns = llmRunValues.max_runs ?? sessionStorage.maxLlmRuns ?? 10;
  const [isLoading, setLoading] = useState(false);
  const organisationId = parseInt(props.params.organisationId, 10);

  const fetchData = useCallback(
    async queryParams => {
      setLoading(true);
      await props.llmRunsCompareFetch(
        parseInt(props.params.organisationId, 10),
        queryParams,
        maxRuns,
      );
      setLoading(false);
    },
    [props.llmRunsCompareFetch, props.params.organisationId, maxRuns],
  );

  useEffect(() => {
    const queryParams = pickLlmRunFiltersFromURL(props.router);

    if (!queryParams.from || !queryParams.max_runs) {
      const additionalParams = {} as Partial<LlmRunFilters>;
      if (!queryParams.from) {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        oneWeekAgo.setHours(0, 0, 0, 0);

        additionalParams.from = formatDateToLocalISOString(oneWeekAgo);
      }

      if (!queryParams.max_runs) {
        additionalParams.max_runs = maxRuns;
      }

      const {pathname, search} = props.location;
      const currentParams = parseQuery(search);
      const newSearch = buildQuery({
        ...currentParams,
        ...additionalParams,
      });
      props.dispatch(push(pathname + newSearch));
    } else {
      fetchData(queryParams);
    }

    return () => {
      setLoading(false);
    };
  }, [
    JSON.stringify(pickLlmRunFiltersFromURL(props.router)),
    JSON.stringify(_.omit(props.location, ["key"])),
  ]);

  const updateUrlParams = obj => {
    const {pathname, search} = props.location;
    const newSearch = getNewSearchParams(obj, search);
    if (search !== newSearch) {
      props.dispatch(push(pathname + newSearch));
    }
  };

  const updateMaxRuns = value => {
    sessionStorage.maxLlmRuns = value;
    updateUrlParams({max_runs: value});
  };

  const inputNames = useMemo(() => {
    if (!isInitialised(props.llmRuns)) {
      return [];
    }
    return Array.from(
      new Set(
        props.llmRuns
          .map(({inputs}) => inputs)
          .flat()
          .map(({name}) => name),
      ),
    );
  }, [JSON.stringify(props.llmRuns)]);

  const shouldRenderContainer = isInitialised([props.llmRuns, props.user]);
  if (!shouldRenderContainer || !props.user.permissions) {
    return (
      <div
        style={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  const permissioner = new Permissioner(props.user);
  if (
    !(permissioner.hasPermission("get-workflows") || permissioner.isAdmin())
  ) {
    return permissioner.getNoPermissionMessage();
  }

  setTitle("LLM Run Compare");

  return (
    <LlmRunCompareComponent
      isLoading={isLoading}
      organisationId={organisationId}
      llmRuns={props.llmRuns}
      inputNames={inputNames}
      values={llmRunValues}
      updateValue={updateUrlParams}
      maxRuns={maxRuns}
      updateMaxRuns={updateMaxRuns}
    />
  );
}

function select(state, props) {
  return {
    params: props.params,
    organisation: state.organisation,
    user: state.user,
    llmRuns: state.llmRuns,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        llmRunsCompareFetch: llmRunsCompareFetch(requestor),
      },
      dispatch,
    ),
  };
}

export default connect(select, mapDispatchToProps)(LlmRunCompareContainer);
